import { useCallback, useEffect, useState } from 'react';
import { fetchCompanyInventoryMovements } from '../../api/companyInventory';
import ListPager from '../common/ListPager';
import {
  CompanyInventoryMovement,
  formatInventoryMovementQty,
  formatInventoryQty,
  inventoryMovementTypeLabel,
} from '../../types/companyInventory';
import { DEFAULT_PAGE_SIZE } from '../../types/page';
import { reportError } from '../../utils/notify';
import '../equipment/EquipTabPanel.css';
import './InventoryStockHistory.css';

interface Props {
  inventoryId: number | null | undefined;
  unit?: string | null;
  emptyHint?: string;
}

function formatOccurredAt(value?: string | null) {
  if (!value) return '-';
  return value.replace('T', ' ').slice(0, 19);
}

function formatBalance(value?: number | string | null, unitSuffix = '') {
  if (value == null || value === '') return '-';
  return `${formatInventoryQty(value)}${unitSuffix}`;
}

function InventoryStockHistory({ inventoryId, unit, emptyHint }: Props) {
  const [rows, setRows] = useState<CompanyInventoryMovement[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(
    async (pageNum = 0) => {
      if (inventoryId == null || inventoryId <= 0) {
        setRows([]);
        setPage(0);
        setTotalPages(0);
        setTotalElements(0);
        setError('');
        return;
      }
      try {
        setLoading(true);
        const result = await fetchCompanyInventoryMovements(inventoryId, {
          page: pageNum,
          size: DEFAULT_PAGE_SIZE,
        });
        setRows(result.content || []);
        setPage(result.page ?? pageNum);
        setTotalPages(result.totalPages ?? 0);
        setTotalElements(result.totalElements ?? 0);
        setError('');
      } catch {
        reportError(setError, '재고 이력을 불러오지 못했습니다.');
        setRows([]);
        setTotalPages(0);
        setTotalElements(0);
      } finally {
        setLoading(false);
      }
    },
    [inventoryId]
  );

  useEffect(() => {
    setPage(0);
    void load(0);
  }, [load]);

  if (inventoryId == null || inventoryId <= 0) {
    return (
      <div className="equip-tab-panel stock-history">
        <p className="equip-tab-panel__hint">
          {emptyHint ||
            '연동된 재고 품목이 없습니다. 완제품은 재고관리와 연결된 뒤 이력이 표시됩니다.'}
        </p>
      </div>
    );
  }

  const unitSuffix = unit?.trim() ? ` ${unit.trim()}` : '';

  return (
    <div className="equip-tab-panel stock-history">
      <div className="equip-tab-panel__toolbar">
        <div>
          <h3>재고이력</h3>
          <p className="equip-tab-panel__hint" style={{ margin: '4px 0 0' }}>
            인수·중간·제품검사와 수동 조정(사유 포함) 이력이 쌓입니다.
          </p>
        </div>
        <button type="button" className="is-ghost" onClick={() => void load(page)} disabled={loading}>
          새로고침
        </button>
      </div>

      {error && <p className="equip-tab-panel__error">{error}</p>}
      {loading ? (
        <p className="equip-tab-panel__hint">불러오는 중...</p>
      ) : rows.length === 0 ? (
        <p className="equip-tab-panel__hint">
          {emptyHint ||
            '아직 입출고 이력이 없습니다. 인수검사·중간검사 승인 또는 재고 수동 조정이 있으면 여기에 기록됩니다.'}
        </p>
      ) : (
        <div className="paged-list paged-list--compact">
          <div className="paged-list__scroll">
            <table className="stock-history__table">
              <thead>
                <tr>
                  <th>일시</th>
                  <th>구분</th>
                  <th>변동</th>
                  <th>잔량</th>
                  <th>Lot</th>
                  <th>사유</th>
                  <th>근거</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const qtyNum = Number(row.qty);
                  const isIn = !Number.isNaN(qtyNum) && qtyNum > 0;
                  const isOut = !Number.isNaN(qtyNum) && qtyNum < 0;
                  return (
                    <tr key={row.movementId}>
                      <td>{formatOccurredAt(row.occurredAt)}</td>
                      <td>
                        {inventoryMovementTypeLabel(row.movementType, row.movementTypeLabel)}
                      </td>
                      <td className={isIn ? 'is-in' : isOut ? 'is-out' : undefined}>
                        {formatInventoryMovementQty(row.qty)}
                        {unitSuffix}
                      </td>
                      <td>{formatBalance(row.balanceAfter, unitSuffix)}</td>
                      <td className={row.lotNo ? undefined : 'is-muted'}>{row.lotNo || '-'}</td>
                      <td className={row.remark ? undefined : 'is-muted'}>{row.remark || '-'}</td>
                      <td className="is-muted">
                        {row.refType ? `${row.refType} #${row.refId ?? '-'}` : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <ListPager
            page={page}
            totalPages={totalPages}
            totalElements={totalElements}
            loading={loading}
            onChange={(next) => {
              setPage(next);
              void load(next);
            }}
          />
        </div>
      )}
    </div>
  );
}

export default InventoryStockHistory;
